import { useQuery } from "@tanstack/react-query";
import { InfoIcon, LinkIcon } from "lucide-react";
import { useMemo, useState } from "react";

import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from "~/components/ui/sheet";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

import { DashboardsPageFrame, KpiCard, KpiGrid } from "./dashboardKit";
import { cnCallReviewQueryOptions } from "./dashboardsReactQuery";
import { CN_CALL_REVIEW_ROW_LIMITS } from "./cnCallReviewTypes";
import type {
  CareNavigationEncounterReviewRow,
  CareNavigationEncounterReviewsResponse,
} from "./cnCallReviewTypes";

const REVIEWED_ENCOUNTERS_TABLE_MAX_HEIGHT_CLASS = "max-h-[min(36rem,calc(12*2.5rem+2.5rem))]";
const DASHBOARD_TIMEZONE = "America/New_York";
const ENCOUNTER_TYPE_OPTIONS = [
  { label: "Ongoing care", value: "ongoing_care_encounter" },
  { label: "Needs assessment", value: "needs_assessment_encounter" },
  { label: "Other", value: "other" },
] as const;
const DEFAULT_ENCOUNTER_TYPES = ENCOUNTER_TYPE_OPTIONS.filter(({ value }) => value !== "other").map(
  ({ value }) => value,
);
const ENCOUNTER_TYPE_LABELS = Object.fromEntries(
  ENCOUNTER_TYPE_OPTIONS.map(({ label, value }) => [value, label]),
);
const NEEDS_REVIEW_LABELS: Record<string, string> = {
  care_plan_not_updated: "Care plan not updated",
  no_follow_up_scheduled: "No follow-up scheduled",
};

// Condensed from the review prompt so a reviewer can see what a rubric line
// actually scores without leaving the dashboard. Anchors are shared: 0 = the
// record shows the behavior was absent, 5 = partial, or not evident in the
// record, 10 = fully handled with specifics visible.
const SCORE_SCALE_MAX = 10;
const SCORE_DEFINITIONS: Record<string, string> = {
  verifications:
    "Patient's full name and date of birth verified, and that the patient is the person on the call. If a caregiver is present, their name and relationship to the patient too.",
  check_ins:
    "Asked about changes since the last contact across health, cognition, mood, behavior, medications, nutrition, daily functioning and caregiver wellbeing, plus home safety: falls, wandering, hazards, managing at home.",
  priorities:
    "Asked what is most difficult or most important right now, considered both patient and caregiver perspectives, and respected the family's right to decline services.",
  care_plan_review:
    "Prior recommendations revisited substantively: what was completed, what failed, what barriers came up, and whether the plan needs revision. Scores the quality of the review, not whether it happened.",
  resources:
    "Delivered something concrete and individualized to an identified need: education, coaching, problem-solving, symptom management, a referral, or coordination with the PCP. Generic advice scores low.",
  safety_response:
    "Urgent risks recognized and escalated. Scores 10 whenever no urgent risk arose, so this average runs high by design and is not a performance signal.",
  follow_through:
    "The encounter ended with specific actions, a responsible person, a timeframe, and a clear statement of what happens next.",
  documentation_match:
    "What the navigator recorded matches what the conversation supports. Scores 5 whenever there is no transcript to compare against.",
};
// The prompt returns the scores in this order; keep the dashboard agreeing with
// it instead of with whatever order the JSON keys happen to arrive in.
const SCORE_KEY_ORDER = Object.keys(SCORE_DEFINITIONS);

function todayIso(referenceDate = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: DASHBOARD_TIMEZONE,
  }).format(referenceDate);
}

function shiftIsoDate(dateIso: string, days: number): string {
  const date = new Date(`${dateIso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function weekRange(referenceDate = new Date()): { end: string; start: string } {
  const today = todayIso(referenceDate);
  const date = new Date(`${today}T12:00:00Z`);
  const mondayOffset = (date.getUTCDay() + 6) % 7;
  const start = shiftIsoDate(today, -mondayOffset);
  return { start, end: shiftIsoDate(start, 6) };
}

export function monthRange(referenceDate = new Date()): { end: string; start: string } {
  const today = todayIso(referenceDate);
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const start = `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-01`;
  const end = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
  return { start, end };
}

function formatEtTimestamp(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DASHBOARD_TIMEZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

function formatDate(value: string): string {
  const date = new Date(`${value}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatScoreKey(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatEncounterType(type: string): string {
  return ENCOUNTER_TYPE_LABELS[type] ?? formatScoreKey(type.replace(/_encounter$/, ""));
}

function formatNeedsReview(flags: string[]): string {
  return flags.length
    ? flags.map((flag) => NEEDS_REVIEW_LABELS[flag] ?? formatScoreKey(flag)).join(", ")
    : "—";
}

// Every column is sortable, so the accessor is one place that knows how to read
// a column out of a row. Scores fall through to the rubric keys.
export function sortValueForRow(
  row: CareNavigationEncounterReviewRow,
  key: string,
): number | string | null {
  const facts = row.facts;
  switch (key) {
    case "encounterAt":
      return new Date(row.encounterAt).getTime();
    case "carespace":
      return row.carespaceId;
    case "encounterType":
      return formatEncounterType(row.encounterType);
    case "callCount":
      return facts?.linkedCallCount ?? row.callIds.length;
    case "connectedMinutes":
      return facts?.connectedMinutes ?? null;
    case "meetsMinimumLength":
      return facts == null ? null : facts.meetsMinimumLength ? 1 : 0;
    case "callsWithUnknownDuration":
      return facts?.callsWithUnknownDuration ?? null;
    case "careplanGoalsCovered":
      return facts?.careplanGoalsCovered ?? null;
    case "careplanUpdated":
      return facts == null ? null : facts.careplanUpdated ? 1 : 0;
    case "followUpScheduled":
      return facts == null ? null : facts.followUpScheduled ? 1 : 0;
    case "needsReview":
      // Most-flagged first when sorting descending is the useful default.
      return row.needsReview.length;
    case "feedback":
      return row.feedback;
    case "navigatorName":
      return row.navigatorName;
    case "promptName":
      return `${row.promptName} ${row.promptVersion ?? ""}`.trim();
    case "reviewedAt":
      return row.reviewedAt ? new Date(row.reviewedAt).getTime() : null;
    case "encounterId":
      return row.encounterId;
    default: {
      if (key.endsWith("-rationale")) {
        return row.scores[key.slice(0, -"-rationale".length)]?.rationale ?? null;
      }
      return row.scores[key]?.value ?? null;
    }
  }
}

export function navigatorNamesForRows(rows: CareNavigationEncounterReviewRow[]): string[] {
  return [...new Set(rows.map((row) => row.navigatorName.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

export type EncounterReviewRowFilters = {
  followUpScheduled?: "scheduled" | "not_scheduled" | undefined;
  needsReview?: "flagged" | "clear" | undefined;
  withTranscripts?: boolean | undefined;
};

export function filterEncounterReviewRows(
  rows: CareNavigationEncounterReviewRow[],
  navigatorName: string,
  filters: EncounterReviewRowFilters = {},
): CareNavigationEncounterReviewRow[] {
  return rows.filter((row) => {
    if (navigatorName && row.navigatorName !== navigatorName) return false;
    if (filters.withTranscripts) {
      const callCount = row.facts?.linkedCallCount ?? row.callIds.length;
      if (callCount === 0) return false;
    }
    if (filters.followUpScheduled) {
      const scheduled = row.facts?.followUpScheduled;
      if (scheduled == null) return false;
      if (scheduled !== (filters.followUpScheduled === "scheduled")) return false;
    }
    if (filters.needsReview) {
      // Unreviewed encounters have no flags yet, not "no flags"; drop them like
      // the follow-up filter does so coverage KPIs stay honest.
      if (!row.isReviewed) return false;
      const flagged = row.needsReview.length > 0;
      if (flagged !== (filters.needsReview === "flagged")) return false;
    }
    return true;
  });
}

const headCellClass = "sticky top-0 z-[1] bg-background px-2 py-2 font-medium whitespace-nowrap";

// Module scope so React keeps the same header buttons across sorts; defined
// inside the table render it remounted them and dropped keyboard focus.
function SortableHead({
  align,
  className,
  columnKey,
  label,
  sort,
  title,
}: {
  align?: "right" | undefined;
  className?: string | undefined;
  columnKey: string;
  label: string;
  sort: { key: string; direction: "asc" | "desc"; onChange: (key: string) => void };
  title?: string | undefined;
}) {
  const active = sort.key === columnKey;
  return (
    <th
      className={cn(headCellClass, align === "right" && "text-right", className)}
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        className="font-medium text-muted-foreground hover:text-foreground"
        type="button"
        title={title}
        onClick={() => sort.onChange(columnKey)}
      >
        {label}
        {active ? (sort.direction === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

function scoreKeysForRows(rows: CareNavigationEncounterReviewRow[]): string[] {
  const keys = [...new Set(rows.flatMap((row) => Object.keys(row.scores)))];
  return keys.sort((left, right) => {
    const leftRank = SCORE_KEY_ORDER.indexOf(left);
    const rightRank = SCORE_KEY_ORDER.indexOf(right);
    if (leftRank === rightRank) return left.localeCompare(right);
    if (leftRank === -1) return 1;
    if (rightRank === -1) return -1;
    return leftRank - rightRank;
  });
}

// The row already carries the encounter's activity id, so the deep link is
// derivable here; it needs no extra API field.
export function encounterActivityLink(row: CareNavigationEncounterReviewRow): string {
  if (!row.carespaceId || !row.encounterId) return "";
  return `https://app.craniometrix.com/carespaces/${encodeURIComponent(
    row.carespaceId,
  )}/activities/${encodeURIComponent(row.encounterId)}`;
}

// A voicemail or a no-answer scores near zero on every rubric line for reasons
// that say nothing about the navigator, so averages are computed over real
// conversations only. Deliberately NOT meetsMinimumLength: that fact is a ~20
// minute "substantive encounter" bar, and excluding every shorter call would
// throw away real 10-minute conversations along with the voicemails.
// ponytail: flat 2-minute floor; make it a filter input if reviewers disagree
// with where the line sits.
const CONVERSATION_MINIMUM_MINUTES = 2;
export function isConversation(row: CareNavigationEncounterReviewRow): boolean {
  const callCount = row.facts?.linkedCallCount ?? row.callIds.length;
  if (callCount === 0) return false;
  const minutes = row.facts?.connectedMinutes;
  return minutes == null || minutes >= CONVERSATION_MINIMUM_MINUTES;
}

function averageScore(rows: CareNavigationEncounterReviewRow[], key: string): number | null {
  const values = rows
    .map((row) => row.scores[key]?.value)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

// The numeric average sits in the neighbouring cell, so the bar is decoration
// for scanning down the column and is hidden from assistive tech.
function ScoreDefinitionPopover({ scoreKey }: { scoreKey: string }) {
  const definition = SCORE_DEFINITIONS[scoreKey];
  if (!definition) return null;
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={`What ${formatScoreKey(scoreKey)} scores`}
            // title keeps the definition on plain hover; the popover is the
            // click-to-pin version.
            title={definition}
            className="text-muted-foreground hover:text-foreground"
          >
            <InfoIcon className="size-3.5" />
          </button>
        }
      />
      <PopoverPopup className="max-w-80 text-xs leading-snug font-normal whitespace-normal text-muted-foreground">
        {definition}
      </PopoverPopup>
    </Popover>
  );
}

function ScoreBar({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const fraction = Math.max(0, Math.min(1, value / SCORE_SCALE_MAX));
  return (
    <span
      aria-hidden="true"
      className="block h-2 w-full min-w-24 overflow-hidden rounded-full bg-muted"
    >
      <span
        className={cn(
          "block h-full rounded-full",
          fraction >= 0.7 ? "bg-emerald-500" : fraction >= 0.4 ? "bg-amber-500" : "bg-red-500",
        )}
        style={{ width: `${(fraction * 100).toFixed(1)}%` }}
      />
    </span>
  );
}

export function EncounterReviewKpis({ rows }: { rows: CareNavigationEncounterReviewRow[] }) {
  const reviewedRows = rows.filter((row) => row.isReviewed);
  // Averages run over reviewed conversations; the counts below stay over every
  // reviewed encounter, because a missing follow-up still counts as a gap.
  const scoredRows = reviewedRows.filter(isConversation);
  const scoreKeys = scoreKeysForRows(scoredRows);
  const coverage = rows.length ? reviewedRows.length / rows.length : null;
  const excluded = reviewedRows.length - scoredRows.length;
  const followUpFlags = reviewedRows.filter((row) =>
    row.needsReview.includes("no_follow_up_scheduled"),
  ).length;
  const carePlanFlags = reviewedRows.filter((row) =>
    row.needsReview.includes("care_plan_not_updated"),
  ).length;

  return (
    <div className="flex flex-col gap-4">
      <KpiGrid>
        <KpiCard
          label="Encounters reviewed / returned eligible encounters"
          value={`${reviewedRows.length.toLocaleString()} / ${rows.length.toLocaleString()}`}
          footnote={
            coverage == null
              ? "No eligible encounters returned"
              : `${(coverage * 100).toFixed(0)}% of returned cohort reviewed`
          }
        />
        <KpiCard
          label="Reviewed encounters without follow-up"
          value={followUpFlags.toLocaleString()}
          footnote="Derived from persisted encounter facts"
        />
        <KpiCard
          label="Reviewed encounters without care-plan update"
          value={carePlanFlags.toLocaleString()}
          footnote="Derived from persisted encounter facts"
        />
      </KpiGrid>

      <article className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
        <h2 className="text-sm font-semibold text-foreground">Average rubric scores</h2>
        <p className="mt-1 mb-3 text-xs text-muted-foreground">
          Averaged over {scoredRows.length.toLocaleString()} reviewed conversation
          {scoredRows.length === 1 ? "" : "s"}
          {excluded > 0
            ? `, excluding ${excluded.toLocaleString()} encounter${
                excluded === 1 ? "" : "s"
              } with no connected call or under ${CONVERSATION_MINIMUM_MINUTES} minutes`
            : ""}
          . Click an info icon for what a metric scores.
        </p>
        {scoreKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No reviewed conversations in this range to score.
          </p>
        ) : (
          <table aria-label="Average rubric scores" className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border/70 text-left text-xs text-muted-foreground">
                <th className="px-2 py-2 font-medium">Metric</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Average</th>
                <th className="px-2 py-2 font-medium">Score</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Encounters</th>
              </tr>
            </thead>
            <tbody>
              {scoreKeys.map((key) => {
                const average = averageScore(scoredRows, key);
                const scored = scoredRows.filter(
                  (row) => typeof row.scores[key]?.value === "number",
                ).length;
                return (
                  <tr key={key} className="border-b border-border/40 last:border-0">
                    <td className="px-2 py-2 font-medium whitespace-nowrap text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        {formatScoreKey(key)}
                        <ScoreDefinitionPopover scoreKey={key} />
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right font-semibold whitespace-nowrap text-foreground">
                      {average == null ? "—" : `${average.toFixed(2)} / ${SCORE_SCALE_MAX}`}
                    </td>
                    <td className="px-2 py-2">
                      <ScoreBar value={average} />
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap text-muted-foreground">
                      {scored.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </article>
    </div>
  );
}

export function EncounterReviewTable({
  cap,
  end,
  onSelectRow,
  rows,
  start,
}: {
  cap: number;
  end: string;
  onSelectRow?: (row: CareNavigationEncounterReviewRow) => void;
  rows: CareNavigationEncounterReviewRow[];
  start: string;
}) {
  const scoreKeys = useMemo(() => scoreKeysForRows(rows), [rows]);
  // Longest conversations first: the encounters worth reading are the ones with
  // enough call on them to judge.
  const [sortKey, setSortKey] = useState("connectedMinutes");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => {
      const leftValue = sortValueForRow(left, sortKey);
      const rightValue = sortValueForRow(right, sortKey);
      const leftMissing = leftValue == null || leftValue === "";
      const rightMissing = rightValue == null || rightValue === "";
      if (leftMissing && rightMissing) return left.encounterId.localeCompare(right.encounterId);
      // Missing values sort last in both directions; a blank cell is never the
      // answer to "show me the worst ones".
      if (leftMissing) return 1;
      if (rightMissing) return -1;
      const comparison =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      if (comparison === 0) return left.encounterId.localeCompare(right.encounterId);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, sortDirection, sortKey]);

  const reviewedRows = sortedRows.filter((row) => row.isReviewed);

  const changeSort = (nextKey: string) => {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(nextKey);
      setSortDirection("desc");
    }
  };

  const sort = { key: sortKey, direction: sortDirection, onChange: changeSort };

  return (
    <article className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      <p className="mb-3 text-xs text-muted-foreground">
        Reviewed encounters are prioritized within the {cap}-encounter result. Facts are computed
        from the encounter record and shown as deterministic scores; AI rubric scores and feedback
        are read from its stored review.
      </p>
      <div className={cn("overflow-auto", REVIEWED_ENCOUNTERS_TABLE_MAX_HEIGHT_CLASS)}>
        <table
          aria-label="Care Navigation encounter quality reviews"
          className="w-full min-w-[1500px] border-collapse text-sm"
        >
          <thead>
            <tr className="border-b border-border/70 text-left text-xs text-muted-foreground">
              <SortableHead sort={sort} columnKey="navigatorName" label="Care navigator" />
              <SortableHead sort={sort} columnKey="encounterAt" label="Encounter time" />
              <SortableHead sort={sort} columnKey="carespace" label="Link" />
              <SortableHead sort={sort} columnKey="encounterType" label="Type" />
              <SortableHead
                sort={sort}
                align="right"
                columnKey="connectedMinutes"
                label="Connected minutes"
              />
              <SortableHead
                sort={sort}
                align="right"
                columnKey="careplanGoalsCovered"
                label="Goals covered"
              />
              <SortableHead sort={sort} columnKey="careplanUpdated" label="Care plan updated" />
              <SortableHead sort={sort} columnKey="followUpScheduled" label="Follow-up scheduled" />
              <SortableHead
                sort={sort}
                className="min-w-[14rem]"
                columnKey="needsReview"
                label="Needs review"
              />
              {scoreKeys.map((key) => [
                <SortableHead
                  sort={sort}
                  key={`${key}-score`}
                  align="right"
                  columnKey={key}
                  label={formatScoreKey(key)}
                  title={SCORE_DEFINITIONS[key]}
                />,
                <SortableHead
                  sort={sort}
                  key={`${key}-rationale`}
                  className="min-w-[16rem]"
                  columnKey={`${key}-rationale`}
                  label={`${formatScoreKey(key)} rationale`}
                  title={SCORE_DEFINITIONS[key]}
                />,
              ])}
              <SortableHead
                sort={sort}
                className="min-w-[16rem]"
                columnKey="feedback"
                label="Feedback"
              />
              <th className={headCellClass}>Review status</th>
              <SortableHead sort={sort} columnKey="promptName" label="Prompt" />
              <SortableHead sort={sort} columnKey="reviewedAt" label="Reviewed at" />
              <SortableHead sort={sort} columnKey="encounterId" label="Encounter ID" />
            </tr>
          </thead>
          <tbody>
            {reviewedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={14 + scoreKeys.length * 2}
                  className="px-2 py-8 text-center text-muted-foreground"
                >
                  No Care Navigation encounter reviews from {formatDate(start)} to {formatDate(end)}
                  .
                </td>
              </tr>
            ) : (
              reviewedRows.map((row) => {
                const facts = row.facts;
                return (
                  <tr
                    key={row.encounterId}
                    className={cn(
                      "border-b border-border/40 last:border-0",
                      onSelectRow && "cursor-pointer hover:bg-muted/40",
                    )}
                    role={onSelectRow ? "button" : undefined}
                    tabIndex={onSelectRow ? 0 : undefined}
                    aria-label={
                      onSelectRow
                        ? `Open CN Call Review for ${formatEtTimestamp(row.encounterAt)}`
                        : undefined
                    }
                    onClick={onSelectRow ? () => onSelectRow(row) : undefined}
                    onKeyDown={
                      onSelectRow
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onSelectRow(row);
                            }
                          }
                        : undefined
                    }
                  >
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {row.navigatorName || "—"}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {formatEtTimestamp(row.encounterAt)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">
                      {encounterActivityLink(row) ? (
                        <a
                          className="inline-flex text-primary hover:text-primary/80"
                          href={encounterActivityLink(row)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Open encounter in the care platform"
                          title="Open encounter in the care platform"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <LinkIcon className="size-3.5" />
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {formatEncounterType(row.encounterType)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right text-foreground">
                      {facts
                        ? facts.connectedMinutes == null
                          ? "Unknown"
                          : `${facts.connectedMinutes.toLocaleString()} min`
                        : "—"}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right text-foreground">
                      {facts?.careplanGoalsCovered ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {facts ? (facts.careplanUpdated ? "Updated" : "Not updated") : "—"}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {facts ? (facts.followUpScheduled ? "Scheduled" : "Not scheduled") : "—"}
                    </td>
                    <td className="max-w-[14rem] px-2 py-2 text-foreground">
                      <span className="line-clamp-3" title={formatNeedsReview(row.needsReview)}>
                        {formatNeedsReview(row.needsReview)}
                      </span>
                    </td>
                    {scoreKeys.flatMap((key) => {
                      const score = row.scores[key];
                      return [
                        <td
                          key={`${row.encounterId}-${key}-score`}
                          className="whitespace-nowrap px-2 py-2 text-right font-semibold text-foreground"
                        >
                          {score?.value ?? "—"}
                        </td>,
                        <td
                          key={`${row.encounterId}-${key}-rationale`}
                          className="max-w-[16rem] px-2 py-2 text-foreground"
                        >
                          <span className="line-clamp-3" title={score?.rationale || undefined}>
                            {score?.rationale || "—"}
                          </span>
                        </td>,
                      ];
                    })}
                    <td className="max-w-[16rem] px-2 py-2 text-foreground">
                      <span className="line-clamp-3" title={row.feedback || undefined}>
                        {row.feedback || "—"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">Reviewed</td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {row.promptName || "—"}
                      {row.promptVersion != null ? ` v${row.promptVersion}` : ""}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {formatEtTimestamp(row.reviewedAt)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-foreground">
                      {row.encounterId}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {reviewedRows.length > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {reviewedRows.length.toLocaleString()} reviewed encounter
          {reviewedRows.length === 1 ? "" : "s"}
        </p>
      ) : null}
    </article>
  );
}

// Preserve the existing component export for consumers of the first dashboard
// version while the endpoint transitions to encounter-level rows.
export const CallReviewTable = EncounterReviewTable;

function EncounterReviewContent({
  data,
  end,
  navigatorName,
  rowFilters,
  start,
}: {
  data: CareNavigationEncounterReviewsResponse;
  end: string;
  navigatorName: string;
  rowFilters: EncounterReviewRowFilters;
  start: string;
}) {
  const filteredRows = filterEncounterReviewRows(data.rows, navigatorName, rowFilters);
  const [selectedRow, setSelectedRow] = useState<CareNavigationEncounterReviewRow | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <EncounterReviewKpis rows={filteredRows} />
      <h2 className="text-sm font-semibold text-foreground">Reviewed encounters</h2>
      <EncounterReviewTable
        key={`${start}-${end}`}
        rows={filteredRows}
        start={start}
        end={end}
        cap={data.cap}
        onSelectRow={setSelectedRow}
      />
      <EncounterReviewSidebar row={selectedRow} onClose={() => setSelectedRow(null)} />
    </div>
  );
}

function EncounterReviewSidebar({
  onClose,
  row,
}: {
  onClose: () => void;
  row: CareNavigationEncounterReviewRow | null;
}) {
  const facts = row?.facts;

  return (
    <Sheet
      open={row != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {row ? (
        <SheetPopup
          aria-label="CN Call Review details"
          side="right"
          className="w-[min(32rem,calc(100vw-2rem))] max-w-lg p-0"
        >
          <SheetHeader className="pr-14">
            <SheetTitle>{formatEncounterType(row.encounterType)} review</SheetTitle>
            <SheetDescription>
              {formatEtTimestamp(row.encounterAt)}
              {row.navigatorName ? ` · ${row.navigatorName}` : ""}
            </SheetDescription>
          </SheetHeader>
          <SheetPanel className="space-y-6 px-6 py-4">
            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Encounter</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Encounter</dt>
                <dd>
                  {encounterActivityLink(row) ? (
                    <a
                      className="text-primary underline-offset-2 hover:underline"
                      href={encounterActivityLink(row)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open encounter
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt className="text-muted-foreground">Carespace</dt>
                <dd>
                  {row.carespaceLink ? (
                    <a
                      className="text-primary underline-offset-2 hover:underline"
                      href={row.carespaceLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open carespace
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt className="text-muted-foreground">Care navigator</dt>
                <dd className="text-foreground">{row.navigatorName || "—"}</dd>
                <dt className="text-muted-foreground">Calls with transcripts</dt>
                <dd className="break-words text-foreground">
                  {row.callIds.length ? row.callIds.join(", ") : "No linked calls"}
                </dd>
                <dt className="text-muted-foreground">Encounter ID</dt>
                <dd className="break-all font-mono text-xs text-foreground">{row.encounterId}</dd>
              </dl>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Deterministic facts</h3>
              {facts ? (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Connected minutes</dt>
                  <dd className="text-foreground">
                    {facts.connectedMinutes == null ? "Unknown" : facts.connectedMinutes}
                  </dd>
                  <dt className="text-muted-foreground">Minimum length</dt>
                  <dd className="text-foreground">{facts.meetsMinimumLength ? "Pass" : "Fail"}</dd>
                  <dt className="text-muted-foreground">Unknown durations</dt>
                  <dd className="text-foreground">{facts.callsWithUnknownDuration}</dd>
                  <dt className="text-muted-foreground">Goals covered</dt>
                  <dd className="text-foreground">{facts.careplanGoalsCovered}</dd>
                  <dt className="text-muted-foreground">Care plan</dt>
                  <dd className="text-foreground">
                    {facts.careplanUpdated ? "Updated" : "Not updated"}
                  </dd>
                  <dt className="text-muted-foreground">Follow-up</dt>
                  <dd className="text-foreground">
                    {facts.followUpScheduled ? "Scheduled" : "Not scheduled"}
                  </dd>
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Deterministic facts are not persisted for this encounter yet.
                </p>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Needs review</h3>
              <p className="text-sm text-foreground">
                {row.needsReview.length ? formatNeedsReview(row.needsReview) : "No flags"}
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">AI rubric scores</h3>
              {Object.keys(row.scores).length ? (
                <div className="space-y-4">
                  {Object.entries(row.scores).map(([key, score]) => (
                    <div key={key} className="rounded-md border border-border/70 p-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <h4 className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                          {formatScoreKey(key)}
                          <ScoreDefinitionPopover scoreKey={key} />
                        </h4>
                        <span className="text-lg font-semibold text-foreground">
                          {score.value} / {SCORE_SCALE_MAX}
                        </span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                        {score.rationale || "No rationale provided."}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No AI scores recorded.</p>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Feedback</h3>
              <p className="whitespace-pre-wrap text-sm text-foreground">
                {row.feedback || "No feedback provided."}
              </p>
            </section>

            <section className="border-t border-border/70 pt-4">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
                <dt className="text-muted-foreground">Prompt</dt>
                <dd className="break-words text-foreground">
                  {row.promptName || "—"}
                  {row.promptVersion != null ? ` v${row.promptVersion}` : ""}
                </dd>
                <dt className="text-muted-foreground">Reviewed at</dt>
                <dd className="text-foreground">{formatEtTimestamp(row.reviewedAt)}</dd>
              </dl>
            </section>
          </SheetPanel>
        </SheetPopup>
      ) : null}
    </Sheet>
  );
}

type CnCallReviewFilterDraft = {
  end: string;
  encounterTypes: string[];
  followUpScheduled: "" | "scheduled" | "not_scheduled";
  limit: number;
  minLengthMinutes: string;
  navigatorName: string;
  needsReview: "" | "flagged" | "clear";
  start: string;
  withTranscripts: boolean;
};

function createInitialFilterDraft(): CnCallReviewFilterDraft {
  const range = monthRange();
  return {
    start: range.start,
    end: range.end,
    encounterTypes: [...DEFAULT_ENCOUNTER_TYPES],
    minLengthMinutes: "",
    navigatorName: "",
    withTranscripts: false,
    followUpScheduled: "",
    needsReview: "",
    limit: CN_CALL_REVIEW_ROW_LIMITS[0],
  };
}

function readMinimumLength(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function areFilterDraftsEqual(
  left: CnCallReviewFilterDraft,
  right: CnCallReviewFilterDraft,
): boolean {
  return (
    left.start === right.start &&
    left.end === right.end &&
    left.minLengthMinutes === right.minLengthMinutes &&
    left.navigatorName === right.navigatorName &&
    left.withTranscripts === right.withTranscripts &&
    left.followUpScheduled === right.followUpScheduled &&
    left.needsReview === right.needsReview &&
    left.limit === right.limit &&
    left.encounterTypes.length === right.encounterTypes.length &&
    left.encounterTypes.every((type, index) => type === right.encounterTypes[index])
  );
}

function CnCallReviewTab() {
  const initialFilters = useMemo(createInitialFilterDraft, []);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const validRange = Boolean(
    draftFilters.start && draftFilters.end && draftFilters.start <= draftFilters.end,
  );
  const validMinimumLength =
    !draftFilters.minLengthMinutes.trim() ||
    readMinimumLength(draftFilters.minLengthMinutes) != null;
  const validFilters = validRange && validMinimumLength && draftFilters.encounterTypes.length > 0;
  const filtersDirty = !areFilterDraftsEqual(draftFilters, appliedFilters);
  const queryParams = useMemo(() => {
    const minLengthMinutes = readMinimumLength(appliedFilters.minLengthMinutes);
    return {
      start: appliedFilters.start,
      end: appliedFilters.end,
      encounterTypes: appliedFilters.encounterTypes,
      limit: appliedFilters.limit,
      ...(minLengthMinutes == null ? {} : { minLengthMinutes }),
    };
  }, [appliedFilters]);
  const query = useQuery(cnCallReviewQueryOptions(queryParams));
  const navigatorOptions = useMemo(() => {
    const names = navigatorNamesForRows(query.data?.rows ?? []);
    if (draftFilters.navigatorName && !names.includes(draftFilters.navigatorName)) {
      names.push(draftFilters.navigatorName);
      names.sort((left, right) => left.localeCompare(right));
    }
    return names;
  }, [draftFilters.navigatorName, query.data?.rows]);

  const updateEncounterType = (type: string) => {
    setDraftFilters((current) => ({
      ...current,
      encounterTypes: current.encounterTypes.includes(type)
        ? current.encounterTypes.filter((selected) => selected !== type)
        : ENCOUNTER_TYPE_OPTIONS.map(({ value }) => value).filter(
            (value) => current.encounterTypes.includes(value) || value === type,
          ),
    }));
  };

  const applyFilters = () => {
    if (!validFilters) return;
    setAppliedFilters({
      ...draftFilters,
      encounterTypes: [...draftFilters.encounterTypes],
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Start
          <input
            aria-label="Encounter review start date"
            type="date"
            value={draftFilters.start}
            max={draftFilters.end}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({ ...current, start: event.target.value }))
            }
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          End
          <input
            aria-label="Encounter review end date"
            type="date"
            value={draftFilters.end}
            min={draftFilters.start}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({ ...current, end: event.target.value }))
            }
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Care navigator
          <select
            aria-label="Filter by care navigator"
            value={draftFilters.navigatorName}
            className="min-w-48 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({ ...current, navigatorName: event.target.value }))
            }
          >
            <option value="">All care navigators</option>
            {navigatorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Minimum length (minutes)
          <input
            aria-label="Minimum call length in minutes"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            placeholder="Any"
            value={draftFilters.minLengthMinutes}
            className="w-32 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                minLengthMinutes: event.target.value,
              }))
            }
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Follow-up
          <select
            aria-label="Filter by follow-up scheduled"
            value={draftFilters.followUpScheduled}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                followUpScheduled: event.target
                  .value as CnCallReviewFilterDraft["followUpScheduled"],
              }))
            }
          >
            <option value="">Any</option>
            <option value="scheduled">Scheduled</option>
            <option value="not_scheduled">Not scheduled</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Needs review
          <select
            aria-label="Filter by needs review"
            value={draftFilters.needsReview}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                needsReview: event.target.value as CnCallReviewFilterDraft["needsReview"],
              }))
            }
          >
            <option value="">Any</option>
            <option value="flagged">Flagged</option>
            <option value="clear">No flags</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Encounters
          <select
            aria-label="Maximum encounters to return"
            value={String(draftFilters.limit)}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
            onChange={(event) =>
              setDraftFilters((current) => ({ ...current, limit: Number(event.target.value) }))
            }
          >
            {CN_CALL_REVIEW_ROW_LIMITS.map((limit) => (
              <option key={limit} value={limit}>
                Up to {limit}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm text-foreground">
          <input
            aria-label="Only encounters with call transcripts"
            type="checkbox"
            checked={draftFilters.withTranscripts}
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                withTranscripts: event.target.checked,
              }))
            }
          />
          Calls with transcripts only
        </label>
        <button
          type="button"
          className="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!validFilters || !filtersDirty}
          onClick={applyFilters}
        >
          Apply filters
        </button>
        <span className="pb-1.5 text-xs text-muted-foreground">
          Eastern Time · current month by default
        </span>
      </div>

      <fieldset className="rounded-lg border border-border/70 bg-background/60 p-3">
        <legend className="px-1 text-xs font-medium text-foreground">Encounter types</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {ENCOUNTER_TYPE_OPTIONS.map(({ label, value }) => (
            <label key={value} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={draftFilters.encounterTypes.includes(value)}
                onChange={() => updateEncounterType(value)}
              />
              {label}
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Ongoing care and needs assessment are selected by default. Changes are staged until you
          apply them.
        </p>
      </fieldset>

      {!validRange || !validMinimumLength || draftFilters.encounterTypes.length === 0 ? (
        <div
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive"
          role="alert"
        >
          {!validRange
            ? "Choose a valid date range."
            : !validMinimumLength
              ? "Minimum length must be a non-negative number."
              : "Select at least one encounter type."}
        </div>
      ) : null}

      {query.isFetching && !query.isPending ? (
        <p className="text-xs text-muted-foreground" role="status">
          Refreshing CN Call Review…
        </p>
      ) : null}

      {query.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading CN Call Review…
        </div>
      ) : null}

      {query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : "Failed to load CN Call Review"}
        </div>
      ) : null}

      {query.data ? (
        <EncounterReviewContent
          data={query.data}
          start={query.data.start}
          end={query.data.end}
          navigatorName={appliedFilters.navigatorName}
          rowFilters={{
            withTranscripts: appliedFilters.withTranscripts,
            followUpScheduled: appliedFilters.followUpScheduled || undefined,
            needsReview: appliedFilters.needsReview || undefined,
          }}
        />
      ) : null}

      {filtersDirty ? (
        <p className="text-xs text-muted-foreground" role="status">
          You have unapplied filter changes.
        </p>
      ) : null}
    </div>
  );
}

export function CnCallReviewDashboard() {
  return (
    <DashboardsPageFrame className="flex flex-col gap-3">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground">CN Call Review</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Care navigator call reviews combining deterministic encounter facts and AI rubric scores.
        </p>
      </div>

      <div
        className="flex flex-wrap gap-2 border-b border-border/70 pb-2"
        role="tablist"
        aria-label="Care Navigation dashboards"
      >
        <button
          type="button"
          role="tab"
          aria-selected="true"
          className="rounded-md border border-primary bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
        >
          CN Call Review
        </button>
      </div>

      <section role="tabpanel" aria-label="CN Call Review">
        <CnCallReviewTab />
      </section>
    </DashboardsPageFrame>
  );
}
